<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :title="$t('map.track_dialog_title') + ' - ' + carrierName"
    width="1200px"
    custom-class="track-dialog"
    :close-on-click-modal="false"
    center
    @close="handleClose"
  >
    <div class="track-map" v-loading="loading">
      <!-- Mapbox container -->
      <div ref="mapEl" class="map-container"></div>

      <!-- Control Panel (top-left) -->
      <el-card class="control-panel" shadow="never">
        <div slot="header" class="panel-header">
          <i class="el-icon-setting"></i>
          <span>{{ $t('map.track_rendering_control') }}</span>
        </div>

        <div class="control-item">
          <label class="control-label">
            {{ $t('map.point_size') }}: <strong>{{ pointSize }}</strong>
          </label>
          <el-slider
            v-model="pointSize"
            :min="3"
            :max="15"
            :step="1"
            @input="onPointSizeChange"
          />
        </div>

        <div class="control-item">
          <label class="control-label">
            {{ $t('map.line_width') }}: <strong>{{ lineWidth }}</strong>
          </label>
          <el-slider
            v-model="lineWidth"
            :min="1"
            :max="10"
            :step="1"
            @input="onLineWidthChange"
          />
        </div>
      </el-card>

      <!-- Statistics Panel (bottom-left) -->
      <div class="stats-panel">
        <div class="stat-item">
          {{ $t('map.track_total_points') }}:
          <span class="stat-value">{{ stats.total }}</span>
        </div>
        <div class="stat-item">
          {{ $t('map.track_avg_speed') }}:
          <span class="stat-value">{{ stats.avg }}</span> {{ $t('map.speed_unit_mph') }}
        </div>
        <div class="stat-item">
          {{ $t('map.track_max_speed') }}:
          <span class="stat-value">{{ stats.max }}</span> {{ $t('map.speed_unit_mph') }}
        </div>
      </div>

      <!-- Speed Legend (bottom-right) -->
      <div class="legend">
        <div class="legend-title">{{ $t('map.track_speed_color') }}</div>
        <div class="legend-bar"></div>
        <div class="legend-labels">
          <span>{{ $t('map.speed_0_mph') }}</span>
          <span>{{ $t('map.max') }}</span>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import Vue from 'vue'
import mapboxgl from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'
import MapboxLanguage from '@mapbox/mapbox-gl-language'
import { mapTrackcarrierTraceState } from '@/api/modules/map'
import ThermohygrometerPopup from './ThermohygrometerPopup.vue'

export default {
  name: 'TrackDialog',
  components: { ThermohygrometerPopup },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    carrierId: {
      type: [String, Number],
      default: null
    },
    carrierName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      map: null,
      trackData: [],
      maxSpeed: 0,
      popup: null,
      popupVm: null,
      pointSize: 7,
      lineWidth: 3,
      refreshTimer: null // 定时刷新计时器
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    },
    stats() {
      if (!this.trackData.length) {
        return { total: 0, avg: '0.00', max: '0.00' }
      }
      const speeds = this.trackData.map((p) => p.speed || 0)
      const sum = speeds.reduce((a, b) => a + b, 0)
      return {
        total: this.trackData.length,
        avg: (sum / speeds.length).toFixed(2),
        max: Math.max(...speeds).toFixed(2)
      }
    }
  },
  watch: {
    visible(val) {
      if (val && this.carrierId) {
        this.resetData()
        this.$nextTick(() => {
          this.loadTrackData()
          this.startRefreshTimer()
        })
      } else {
        this.stopRefreshTimer()
      }
    }
  },
  beforeDestroy() {
    // 清除定时刷新计时器
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
      this.refreshTimer = null
    }
    this.cleanupPopup()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    // 重置数据
    resetData() {
      this.trackData = []
      this.maxSpeed = 0
      this.pointSize = 7
      this.lineWidth = 3
      this.cleanupPopup()
      if (this.map) {
        this.map.remove()
        this.map = null
      }
    },

    // 启动定时刷新（5分钟）
    startRefreshTimer() {
      this.stopRefreshTimer()
      this.refreshTimer = setInterval(() => {
        this.refreshTrackData()
      }, 5 * 60 * 1000) // 5分钟
    },

    // 停止定时刷新
    stopRefreshTimer() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
        this.refreshTimer = null
      }
    },

    // 刷新轨迹数据（清除原有路线和点，再渲染）
    async refreshTrackData() {
      if (!this.carrierId) return

      // 清除原有的路线和点
      this.removeLayer('track-points')
      this.removeSource('track-points')
      this.removeLayer('track-line')
      this.removeSource('track-line')
      this.cleanupPopup()

      // 重新加载数据
      await this.loadTrackData()
    },

    // 加载轨迹数据
    async loadTrackData() {
      this.loading = true
      try {
        const res = await mapTrackcarrierTraceState({ id: this.carrierId })
        if (res && res.result && res.result.traceList && res.result.traceList.length > 0) {
          // 映射字段名：time->timestamp, lat->latitude, lng->longitude
          const rawPoints = res.result.traceList.map((item, index) => ({
            timestamp: item.time,
            latitude: item.lat,
            longitude: item.lng,
            speed: item.speed,
            ex: item.ex,
            pointIndex: index
          }))

          // 使用垂距限差法进行轨迹抽稀
          this.trackData = this.simplifyTrack(rawPoints, 1)
          this.maxSpeed = Math.max(...this.trackData.map((p) => p.speed || 0))
          this.$nextTick(() => {
            this.initMap()
          })
        } else {
          this.trackData = []
          this.loading = false
        }
      } catch (error) {
        console.error('Failed to load track data:', error)
        this.loading = false
      }
    },

    /**
     * 前端轨迹抽稀算法 (垂距限差法)
     * @param {Array} points 原始轨迹点数组
     * @param {Number} thresholdMeters 距离阈值(米)
     * @returns {Array} 抽稀后的轨迹点
     */
    simplifyTrack(points, thresholdMeters = 1) {
      if (points.length < 3) return points

      // 将米转换为近似的经纬度度数 (1度约等于111公里)
      const threshold = thresholdMeters / 111000

      let result = [points[0]]
      let prevPoint = points[0]

      for (let i = 1; i < points.length - 1; i++) {
        const currPoint = points[i]
        const nextPoint = points[i + 1]

        const dx = nextPoint.longitude - prevPoint.longitude
        const dy = nextPoint.latitude - prevPoint.latitude
        const len = Math.sqrt(dx * dx + dy * dy)

        if (len === 0) {
          result.push(currPoint)
          prevPoint = currPoint
          continue
        }

        const distance = Math.abs(
          dy * currPoint.longitude -
          dx * currPoint.latitude +
          nextPoint.longitude * prevPoint.latitude -
          nextPoint.latitude * prevPoint.longitude
        ) / len

        if (distance > threshold) {
          result.push(currPoint)
          prevPoint = currPoint
        }
      }
      result.push(points[points.length - 1])
      return result
    },

    // 初始化地图
    initMap() {
      const token = process.env.VUE_APP_MAPBOX_TOKEN
      if (!token) {
        this.loading = false
        return
      }

      const container = this.$refs.mapEl
      if (!container) {
        this.loading = false
        return
      }

      mapboxgl.accessToken = token
      mapboxgl.telemetryEnabled = false

      // 计算地图中心
      let center = [113.51, 23.17]
      if (this.trackData.length > 0) {
        const lngs = this.trackData.map(p => p.longitude)
        const lats = this.trackData.map(p => p.latitude)
        center = [
          (Math.max(...lngs) + Math.min(...lngs)) / 2,
          (Math.max(...lats) + Math.min(...lats)) / 2
        ]
      }

      try {
        this.map = new mapboxgl.Map({
          container: container,
          style: 'mapbox://styles/mapbox/streets-v12',
          center: center,
          zoom: 12,
          attributionControl: false
        })

        // 添加导航控件
        this.map.addControl(new mapboxgl.NavigationControl())

        // 添加语言支持
        const language = this.getMapboxLanguage()
        const languagePlugin = new MapboxLanguage({
          defaultLanguage: language
        })
        this.map.addControl(languagePlugin)

        this.map.on('load', () => {
          this.loading = false
          this.renderTrack()
          this.fitBounds()
        })

        this.map.on('error', (e) => {
          console.error('Track map error:', e)
          this.loading = false
        })
      } catch (err) {
        console.error('Failed to initialize track map:', err)
        this.loading = false
      }
    },

    // 获取 Mapbox 语言设置
    getMapboxLanguage() {
      const lang = this.$i18n.locale || 'zh_CN'
      const langMap = {
        'zh_CN': 'zh-Hans',
        'en_US': 'en',
        'es': 'es',
        'ja': 'ja'
      }
      return langMap[lang] || 'en'
    },

    // 渲染轨迹
    renderTrack() {
      if (!this.trackData.length) return

      const pointsGeoJSON = {
        type: 'FeatureCollection',
        features: this.trackData.map((point) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [point.longitude, point.latitude]
          },
          properties: {
            ...point,
            color: this.getColorBySpeed(point.speed || 0, this.maxSpeed)
          }
        }))
      }

      const lineGeoJSON = {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: this.trackData.map((p) => [p.longitude, p.latitude])
        }
      }

      // Remove existing layers/sources if re-rendering
      this.removeLayer('track-points')
      this.removeSource('track-points')
      this.removeLayer('track-line')
      this.removeSource('track-line')

      // Track line
      this.map.addSource('track-line', { type: 'geojson', data: lineGeoJSON })
      this.map.addLayer({
        id: 'track-line',
        type: 'line',
        source: 'track-line',
        layout: { 'line-join': 'round', 'line-cap': 'round' },
        paint: {
          'line-color': '#4264fb',
          'line-width': this.lineWidth,
          'line-opacity': 0.6
        }
      })

      // Track points
      this.map.addSource('track-points', {
        type: 'geojson',
        data: pointsGeoJSON
      })
      this.map.addLayer({
        id: 'track-points',
        type: 'circle',
        source: 'track-points',
        paint: {
          'circle-radius': this.pointSize,
          'circle-color': ['get', 'color'],
          'circle-stroke-color': '#ffffff',
          'circle-stroke-width': 2,
          'circle-opacity': 0.9
        }
      })

      this.bindPointEvents()
    },

    bindPointEvents() {
      // Cursor feedback
      this.map.off('mouseenter', 'track-points', this.onMouseEnter)
      this.map.off('mouseleave', 'track-points', this.onMouseLeave)
      this.map.off('click', 'track-points', this.onPointClick)

      this.map.on('mouseenter', 'track-points', this.onMouseEnter)
      this.map.on('mouseleave', 'track-points', this.onMouseLeave)
      this.map.on('click', 'track-points', this.onPointClick)
    },

    onMouseEnter() {
      this.map.getCanvas().style.cursor = 'pointer'
    },
    onMouseLeave() {
      this.map.getCanvas().style.cursor = ''
    },
    onPointClick(e) {
      const feature = e.features[0]
      const props = feature.properties
      // pointIndex may arrive as a string from GeoJSON properties — coerce it
      const pointIndex =
        typeof props.pointIndex === 'number'
          ? props.pointIndex
          : parseInt(props.pointIndex, 10)
      const timestamp =
        typeof props.timestamp === 'number'
          ? props.timestamp
          : parseInt(props.timestamp, 10)
      const speed =
        typeof props.speed === 'number'
          ? props.speed
          : parseFloat(props.speed)

      const sensor = this.getSensorData(timestamp, pointIndex)

      this.openPopup(feature.geometry.coordinates, {
        pointIndex,
        temperature: sensor.temperature,
        humidity: sensor.humidity,
        speed,
        timeStr: this.formatTime(timestamp)
      })
    },

    // Popup (Vue-rendered inside Mapbox Popup)
    openPopup(coords, payload) {
      this.cleanupPopup()

      // 1. Create the Mapbox Popup shell with an empty DOM node.
      const popupNode = document.createElement('div')
      this.popup = new mapboxgl.Popup({
        offset: 20,
        closeButton: true,
        maxWidth: '320px'
      })
        .setLngLat(coords)
        .setDOMContent(popupNode)
        .addTo(this.map)

      // 2. Mount the Vue component into that node.
      const PopupCtor = Vue.extend(ThermohygrometerPopup)
      this.popupVm = new PopupCtor({
        propsData: payload,
        parent: this
      })
      this.popupVm.$mount(popupNode)

      // 3. Clean up the Vue instance when the popup is closed.
      this.popup.on('close', this.cleanupPopup.bind(this))
    },

    cleanupPopup() {
      if (this.popupVm) {
        this.popupVm.$destroy()
        this.popupVm = null
      }
      if (this.popup) {
        try {
          this.popup.remove()
        } catch (e) {
          /* already removed */
        }
        this.popup = null
      }
    },

    // Bounds
    fitBounds() {
      if (!this.trackData.length) return
      const bounds = new mapboxgl.LngLatBounds()
      this.trackData.forEach((p) => bounds.extend([p.longitude, p.latitude]))
      this.map.fitBounds(bounds, {
        padding: { top: 80, bottom: 80, left: 320, right: 80 }
      })
    },

    // Layer/source helpers
    removeLayer(id) {
      if (this.map && this.map.getLayer(id)) this.map.removeLayer(id)
    },
    removeSource(id) {
      if (this.map && this.map.getSource(id)) this.map.removeSource(id)
    },

    // Slider handlers
    onPointSizeChange(val) {
      if (this.map && this.map.getLayer('track-points')) {
        this.map.setPaintProperty('track-points', 'circle-radius', val)
      }
    },
    onLineWidthChange(val) {
      if (this.map && this.map.getLayer('track-line')) {
        this.map.setPaintProperty('track-line', 'line-width', val)
      }
    },

    // 颜色工具
    getColorBySpeed(speed, maxSpeed) {
      const safeMax = maxSpeed > 0 ? maxSpeed : 1
      const ratio = Math.min(speed / safeMax, 1)

      if (ratio < 0.33) {
        const r = Math.round((255 * ratio) / 0.33)
        return `rgb(${r}, 255, 0)`
      }
      if (ratio < 0.66) {
        const g = Math.round(255 - 255 * ((ratio - 0.33) / 0.33) * 0.5)
        return `rgb(255, ${g}, 0)`
      }
      const g = Math.round(128 - 128 * ((ratio - 0.66) / 0.34))
      return `rgb(255, ${g}, 0)`
    },

    // 传感器数据
    getSensorData(timestamp, pointIndex) {
      // Base temperature 22°C (71.6°F) with slight sinusoidal variation
      const baseTempC = 22
      const timeVar = (timestamp / 1000000000) % 100
      const tempC = baseTempC + Math.sin(timeVar) * 3 + (pointIndex % 10) * 0.2

      // Base humidity 58% with cosine variation
      const baseHum = 58
      const hum = baseHum + Math.cos(timeVar) * 10 + (pointIndex % 15)

      return {
        temperature: tempC.toFixed(1),
        humidity: Math.max(30, Math.min(90, hum)).toFixed(1)
      }
    },

    // 时间格式化
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}`
    },

    // 关闭弹窗
    handleClose() {
      this.cleanupPopup()
      if (this.map) {
        this.map.remove()
        this.map = null
      }
      this.trackData = []
      this.$emit('close')
    }
  }
}
</script>

<style lang="scss" scoped>
.track-map {
  position: relative;
  width: 100%;
  height: 700px;
}

.map-container {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

// Control Panel
.control-panel {
  position: absolute;
  top: 20px;
  left: 20px;
  width: 260px;
  z-index: 10;
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  ::v-deep .el-card__header {
    padding: 10px 14px;
    border-bottom: 1px solid #ebeef5;
  }

  ::v-deep .el-card__body {
    padding: 10px 14px 4px;
  }

  .panel-header {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    font-weight: 600;
    color: #1f2430;

    i {
      color: #4264fb;
    }
  }

  .control-item {
    padding: 10px;

    &:last-child {
      margin-bottom: 5px;
    }
  }

  .control-label {
    display: block;
    margin-bottom: 2px;
    font-size: 12px;
    color: #4a5160;

    strong {
      color: #4264fb;
      font-weight: 600;
    }
  }
}

// Statistics Panel
.stats-panel {
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: #ffffff;
  border-radius: 12px;
  padding: 14px 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 10;
  font-size: 13px;
  color: #4a5160;

  .stat-item {
    display: inline-block;
    margin-right: 20px;

    &:last-child {
      margin-right: 0;
    }
  }

  .stat-value {
    color: #4264fb;
    font-weight: 600;
    font-size: 15px;
    margin: 0 2px;
  }
}

// Legend
.legend {
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: #ffffff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 10;

  .legend-title {
    font-size: 13px;
    color: #4a5160;
    margin-bottom: 8px;
    font-weight: 500;
  }

  .legend-bar {
    width: 200px;
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(to right, #00ff00, #ffff00, #ff0000);
    margin-bottom: 5px;
  }

  .legend-labels {
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: #8a90a0;
  }
}

// Mapbox popup cosmetic overrides
::v-deep .mapboxgl-popup-content {
  border-radius: 14px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  border: none;
}

::v-deep .mapboxgl-popup-close-button {
  font-size: 18px;
  color: #fff;
  padding: 4px 8px;
  top: 6px;
  right: 6px;

  &:hover {
    background: transparent;
    color: rgba(255, 255, 255, 0.8);
  }
}
</style>

<style lang="scss">
/* 轨迹弹窗全局样式 */
.track-dialog {
  margin-top: 5vh !important;
  .el-dialog__body {
    padding: 10px;
  }
}
</style>
